import { ExecutionStep, ExecutionStepResult, PipelineExecutionState } from '@/interfaces/pipelineExecution.interface';
import { Paper } from '@mui/material';
import React from 'react';
import EmptyContextPanel, { SuggestionItem } from './emptyContextPanel';
import EntityContextHeader from './entityContextHeader';
import EntityListPanel from './entityListPanel';
import PipelineExecutionPanel from './pipelineExecutionPanel';
import SingleEntityPanel from './singleEntityPanel';
import { HistoryEntry, RightPanelContext } from './types';

export * from './types';

type Props = {
  rightPanelContext: RightPanelContext;
  pipelineExecutionState?: PipelineExecutionState;
  selectedStep?: ExecutionStep | null;
  executingStepId?: string | null;
  stepExecutionResponse?: ExecutionStepResult | null;
  suppressEvents?: boolean;
  onToggleSuppressEvents?: (value: boolean) => void;
  onSelectStep: (step: ExecutionStep) => void;
  onExecuteStep: (
    step: ExecutionStep,
    options?: { inputOverride?: any; reingest?: boolean; suppressEvents?: boolean },
  ) => void;
  onRestartFromPoint: (step: ExecutionStep, eventDataId?: string | number) => void;
  onClearStepExecutionResponse: () => void;
  onChange: () => void;
  onPipelineSelect: (pipelineId: string) => void;
  onEntitySelect: (entityInfo: any) => void;
  onResetContext?: () => void;
  onSelectSuggestion?: (item: SuggestionItem) => void;
  onSelectPrompt?: (promptText: string) => void;
  // History navigation props
  canGoBack?: boolean;
  canGoForward?: boolean;
  onHistoryBack?: () => void;
  onHistoryForward?: () => void;
  historyList?: HistoryEntry[];
  currentHistoryIndex?: number;
  onHistoryJump?: (index: number) => void;
};

const EntityContextPanel: React.FC<Props> = ({
  rightPanelContext,
  pipelineExecutionState,
  selectedStep,
  executingStepId,
  stepExecutionResponse,
  suppressEvents,
  onToggleSuppressEvents,
  onSelectStep,
  onExecuteStep,
  onRestartFromPoint,
  onClearStepExecutionResponse,
  onChange,
  onPipelineSelect,
  onEntitySelect,
  onResetContext,
  onSelectSuggestion,
  onSelectPrompt,
  onHistoryBack,
  onHistoryForward,
  historyList = [],
  currentHistoryIndex = 0,
  onHistoryJump,
}) => {
  const currentStep = selectedStep ?? pipelineExecutionState?.steps?.[0] ?? null;
  return (
    <Paper
      elevation={2}
      sx={{
        flexGrow: 1,
        width: '58%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid #e2e8f0',
      }}
    >
      <EntityContextHeader
        historyList={historyList}
        currentHistoryIndex={currentHistoryIndex}
        rightPanelContext={rightPanelContext}
        pipelineExecutionState={pipelineExecutionState}
        selectedStep={currentStep}
        onHistoryBack={onHistoryBack}
        onHistoryForward={onHistoryForward}
        onHistoryJump={onHistoryJump}
        onResetContext={onResetContext}
      />
      {rightPanelContext.type === 'pipeline' && pipelineExecutionState && (
        <PipelineExecutionPanel
          pipelineExecutionState={pipelineExecutionState}
          selectedStep={currentStep}
          executingStepId={executingStepId}
          stepExecutionResponse={stepExecutionResponse}
          suppressEvents={suppressEvents}
          onToggleSuppressEvents={onToggleSuppressEvents}
          onSelectStep={onSelectStep}
          onClearStepExecutionResponse={onClearStepExecutionResponse}
          onExecuteStep={onExecuteStep}
          onRestartFromPoint={onRestartFromPoint}
          onChange={onChange}
        />
      )}

      {rightPanelContext.type === 'single' && (
        <SingleEntityPanel
          rightPanelContext={rightPanelContext}
          executingStepId={executingStepId}
          stepExecutionResponse={stepExecutionResponse}
          onClearStepExecutionResponse={onClearStepExecutionResponse}
          onExecuteAction={onExecuteStep}
          onChange={onChange}
        />
      )}

      {rightPanelContext.type === 'entity_list' && rightPanelContext.entityList && (
        <EntityListPanel
          rightPanelContext={rightPanelContext}
          onPipelineSelect={onPipelineSelect}
          onEntitySelect={onEntitySelect}
        />
      )}
      {rightPanelContext.type === 'empty' && (
        <EmptyContextPanel onSelectSuggestion={onSelectSuggestion} onSelectPrompt={onSelectPrompt} />
      )}
    </Paper>
  );
};

export default EntityContextPanel;
